import Header from "./Header";
import Pages from "./Pages";
import Rows from "./Rows";
import UpdateModal from "./UpdateModal";
import InfoModal from "./InfoModal";
import DeleteModal from "./DeleteModal";
import { checkAuthentication } from "./Auth";
import { getItem, originalExists, search, createFormItem, getConfigFile, uploadConfig, checkUrl, indexRules, ConcurrentModificationError } from "./utils/dashboardUtils"
import { validateOriginalPattern, scanRulesForRisky } from "./utils/regexValidator";
import { useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate } from "react-router-dom";

import { showSuccessMessage, showErrorMessage, showWarningMessage } from './utils/exceptionUtils';
import { Trash, PencilSquare, Globe, InfoCircle, ExclamationTriangleFill } from 'react-bootstrap-icons';

import './static/css/common.css';

let configJson;
let configIndex = { byId: new Map(), byOriginal: new Map() };
let currentETag = null;
let currentUser;
let pageSize = 200;

const Dashboard = () => {
    const effectRan = useRef(false);
    const isAuthenticated = true;
    const navigate = useNavigate();
    const [rows, updateRows] = useState();
    const [renderFirstPage, updateRenderFirstPage] = useState(false);
    const [stateConfig, setStateConfig] = useState([]);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [originalValue, setOriginalValue] = useState("");    
    const [redirectValue, setRedirectValue] = useState("");    
    const [searchValue, setSearchValue] = useState("");
    const [currentItem, setCurrentItem] = useState(); //getInitItem()
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [riskyRuleIds, setRiskyRuleIds] = useState(new Set());

    //console.log(JSON.stringify(currentUser, null, 2));
    //console.log(currentUser?.signInUserSession?.idToken?.payload?.name);
    //console.log("Dashboard searchValue: " + searchValue);

    const changeHandler = useCallback((e) => {
        const newCurrenItem = JSON.parse(JSON.stringify(currentItem));
        newCurrenItem[e.target.name] = e.target.value;
        setCurrentItem(newCurrenItem);
    }, [ currentItem ]);

    const openUpdateModal = useCallback((id) => {
        setCurrentItem(getItem(id, configIndex));
        setIsUpdateModalOpen(true);
    }, []);

    const openInfoModal = useCallback((id) => {
        setCurrentItem(getItem(id, configIndex));
        setIsInfoModalOpen(true);
    }, []);

    const openDeleteModal = useCallback((id) => {
        setCurrentItem(getItem(id, configIndex));
        setIsDeleteModalOpen(true);
    }, []);

    const openInNewTab = (url) => {
        window.open(checkUrl(url), "_blank", "noreferrer");
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (isSaving) return;

        const elements = event.currentTarget.elements;
        const id = elements.id?.value;
        const original = elements.original.value;
        const domain = elements.domain?.value;
        const redirect = elements.redirect.value;

        if ((domain && domain === "https://yle.fi" && !original) || !redirect) {
            showErrorMessage("Virhe", "Alkuperäinen URL ja/tai Uudelleenohjaus eivät voi olla tyhjiä.");
            return;
        }
        if (originalExists(domain, original, id, configIndex)) {
            showErrorMessage("Virhe", `Polku (${original}) on jo määritetty.`);
            return;
        }

        const patternError = validateOriginalPattern(original);
        if (patternError) {
            showErrorMessage("Virhe", patternError);
            return;
        }

        setIsSaving(true);
        try {
            if (id) {
                await updateExistingRule(elements);
                const res = search(searchValue, configJson);
                const pageItems = res.slice((currentIndex*pageSize), ((currentIndex*pageSize)+pageSize));
                setStateConfig(pageItems);
                setRiskyRuleIds(scanRulesForRisky(configJson));
                setIsUpdateModalOpen(false);
                showSuccessMessage("OK", "Sääntö päivitettiin.");
            } else {
                await addNewRule(elements);
                setOriginalValue("");
                setRedirectValue("");
                setRiskyRuleIds(scanRulesForRisky(configJson));
                updateRenderFirstPage(!renderFirstPage);
                showSuccessMessage("OK", "Uusi sääntö lisättiin.");
            }
        } catch (error) {
            if (error instanceof ConcurrentModificationError) {
                showErrorMessage(
                    "Virhe",
                    "Joku muu tallensi muutoksia samanaikaisesti. Lataa sivu uudelleen (F5) ja tee muutos uudestaan."
                );
            } else {
                console.error('error handling form', error);
                showErrorMessage("Virhe", "Config-tiedostoa ei voitu päivittää.");
            }
        } finally {
            setIsSaving(false);
        }
    }

    const handleDelete = async (event) => {
        event.preventDefault();
        if (isSaving) return;

        setIsSaving(true);
        try {
            await removeItem(event.currentTarget.elements);
            const res = search(searchValue, configJson);
            const pageItems = res.slice((currentIndex*pageSize), ((currentIndex*pageSize)+pageSize));
            setStateConfig(pageItems);
            setRiskyRuleIds(scanRulesForRisky(configJson));
            setIsDeleteModalOpen(false);
            showWarningMessage("OK", "Sääntö poistettiin.");
        } catch (error) {
            if (error instanceof ConcurrentModificationError) {
                showErrorMessage(
                    "Virhe",
                    "Joku muu tallensi muutoksia samanaikaisesti. Lataa sivu uudelleen (F5) ja tee muutos uudestaan."
                );
            } else {
                console.error('error handling delete form', error);
                showErrorMessage("Virhe", "Sääntöä ei voitu poistaa.");
            }
        } finally {
            setIsSaving(false);
        }
    };

    const updateItems = useCallback((json, risky) => {
        updateRows(createRows(json, risky, openUpdateModal, openInNewTab, openInfoModal, openDeleteModal));
    // eslint-disable-next-line
    }, []);

    useEffect(() => {
        //console.log("SearchValue or RenderFirstPage has changed");
        const res = search(searchValue, configJson);
        const pageItems = res.slice(0, pageSize);
        setCurrentPage(0);
        setPageCount(Math.ceil(res.length / pageSize));
        setStateConfig(pageItems);                   
    }, [ searchValue, renderFirstPage ]);

    useEffect(() => {
        //console.log("CurrentIndex has changed");
        const res = search(searchValue, configJson);
        const pageItems = res.slice((currentIndex*pageSize), ((currentIndex*pageSize)+pageSize));
        setCurrentPage(currentIndex);
        setPageCount(Math.ceil(res.length / pageSize));
        setStateConfig(pageItems);
    // eslint-disable-next-line                 
    }, [ currentIndex ]);

    useEffect(() => {
        updateItems(stateConfig, riskyRuleIds);
    // eslint-disable-next-line
    }, [ stateConfig, riskyRuleIds ]);

    useEffect(() => {
        if (!effectRan.current) {
            checkAuthentication().then((result) => {
            if (result) currentUser = result;
            else {
                console.log("User not authenticated");
                navigate("/");
            }
            });

            getConfigFile().then((result) => {
                configJson = result.rules;
                configIndex = indexRules(configJson);
                currentETag = result.eTag;
                const risky = scanRulesForRisky(configJson);
                setRiskyRuleIds(risky);
                if (risky.size > 0) {
                    showWarningMessage(
                        "Huomio",
                        `${risky.size} sääntöä sisältää riskialtiin regex-kuvion. Avaa muokkaustila ja korjaa.`
                    );
                }
                updateRenderFirstPage(!renderFirstPage);
            });
        }
        return () => effectRan.current = true;
        // eslint-disable-next-line
    }, [ ]);

    return (
        <>
        <Header isAuthenticated={isAuthenticated} searchValue={searchValue} setSearchValue={setSearchValue} setCurrentPage={setCurrentPage}/>   
        <Pages pageCount={pageCount} currentPage={currentPage} setCurrentPage={setCurrentPage} setCurrentIndex={setCurrentIndex}/>    
        <Rows rows={rows} handleSubmit={handleSubmit} originalValue={originalValue} setOriginalValue={setOriginalValue} redirectValue={redirectValue} setRedirectValue={setRedirectValue} isSaving={isSaving} />
        <UpdateModal isUpdateModalOpen={isUpdateModalOpen} setIsUpdateModalOpen={setIsUpdateModalOpen} currentItem={currentItem} changeHandler={changeHandler} handleSubmit={handleSubmit} isSaving={isSaving} />
        <InfoModal isInfoModalOpen={isInfoModalOpen} setIsInfoModalOpen={setIsInfoModalOpen} currentItem={currentItem} />
        <DeleteModal isDeleteModalOpen={isDeleteModalOpen} setIsDeleteModalOpen={setIsDeleteModalOpen} currentItem={currentItem} handleDelete={handleDelete} isSaving={isSaving} />
        </>
    );
};

const createRows = (configJson, riskyRuleIds, openUpdateModal, openInNewTab, openInfoModal, openDeleteModal) => {

    if (!configJson) return;
    return (
        <>
        { configJson.map((item) => {
            const isRisky = riskyRuleIds && riskyRuleIds.has(item.id);
            return (
            <tr key={item.id}>
                <td colSpan={2}>
                    {isRisky && (
                        <ExclamationTriangleFill
                            className="icon"
                            style={{ color: '#dc3545', marginRight: '6px' }}
                            title="Riskialtis regex-kuvio: voi aiheuttaa Lambda-timeoutin. Avaa muokkaus ja korjaa."
                        />
                    )}
                    {parseDomain(item)}
                </td>
                <td>{item.redirect}</td>
                <td>{item.statusCode === "301" ? "Pysyvä (301)" : "Väliaikainen (302)"}</td>
                <td>{item.startTime}</td>
                <td>{item.endTime}</td>
                <td>
                    <Globe className="icon" onClick={() => openInNewTab(item.original)}/>
                    &nbsp;&nbsp;&nbsp;
                    <PencilSquare  className="icon" onClick={() => openUpdateModal(item.id)}/>
                    &nbsp;&nbsp;&nbsp;
                    <InfoCircle  className="icon" onClick={() => openInfoModal(item.id)}/>
                    &nbsp;&nbsp;&nbsp;
                    <Trash  className="icon" onClick={() => openDeleteModal(item.id)}/>
                </td>
            </tr>
            );
        })}
        </>
    );
};

const parseDomain = (item) => {
    return item.original.startsWith("http") ? item.original : "https://yle.fi" + item.original;
}

const refreshBaseline = async () => {
    const fresh = await getConfigFile();
    configJson = fresh.rules;
    configIndex = indexRules(configJson);
    currentETag = fresh.eTag;
};

const addNewRule = async (elements) => {

    const newItem = createFormItem(elements);
    newItem.modified = new Date().toISOString();
    newItem.user = currentUser?.signInUserSession?.idToken?.payload?.email;
    configJson.unshift(newItem);
    try {
        await uploadConfig(configJson, currentETag);
    } catch (err) {
        configJson.shift();
        throw err;
    }
    await refreshBaseline();
};

const updateExistingRule = async (elements) => {

    const formItem = createFormItem(elements);
    const item = getItem(elements.id.value, configJson);
    const previous = { ...item };

    item.original = formItem.original;
    item.redirect = formItem.redirect
    item.statusCode = formItem.statusCode;
    item.startTime = formItem.startTime;
    item.endTime = formItem.endTime;
    item.modified = new Date().toISOString();
    item.user = currentUser?.signInUserSession?.idToken?.payload?.email;

    try {
        await uploadConfig(configJson, currentETag);
    } catch (err) {
        Object.assign(item, previous);
        throw err;
    }
    await refreshBaseline();
};

const removeItem = async (elements) => {

    const id = elements.id.value;
    let removedIndex = -1;
    let removedItem = null;
    for (const [index, item] of configJson.entries()) {
        if (item.id === id) {
            removedIndex = index;
            removedItem = item;
            configJson.splice(index, 1);
            break;
        }
    }
    try {
        await uploadConfig(configJson, currentETag);
    } catch (err) {
        if (removedItem) configJson.splice(removedIndex, 0, removedItem);
        throw err;
    }
    await refreshBaseline();
};

export default Dashboard;
